import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Chip,
  Button,
  Snackbar,
  Alert,
  Divider,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import moment from 'moment';
import SearchField from '../components/SearchField';
import { CardSkeleton } from '../components/SkeletonLoader';
import ErrorState from '../components/ErrorState';
import NoEntries from '../components/NoEntries';
import { useTranslation } from 'react-i18next';
import AppStyles from '../styles/AppStyles';
import { ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3000';

function Medication() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [error] = useState('');
  const [petId, setPetId] = useState(() => localStorage.getItem('currentPetId') || '');
  const [copied, setCopied] = useState(false);
  const queryClient = useQueryClient();

  const token = typeof window !== 'undefined' ? localStorage.getItem('vetToken') : null;
  const currentPetId = typeof window !== 'undefined' ? (localStorage.getItem('currentPetId') || petId) : petId;
  useEffect(() => setPetId(currentPetId), [currentPetId]);

  const summaryQuery = useQuery({
    queryKey: ['summary', petId],
    enabled: !!petId,
    queryFn: async () => {
      const resp = await fetch(`${API_BASE}/api/sharing/me/patients/${petId}/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.message || t('medication.failedToLoad'));
      return data?.data || null;
    },
    staleTime: 60000,
  });

  // Listen for pet changes like LabResults/Dashboard and invalidate cache
  useEffect(() => {
    const handlePetChange = () => {
      const currentPetId = localStorage.getItem('currentPetId');
      if (currentPetId && currentPetId !== petId) {
        setPetId(currentPetId);
        queryClient.invalidateQueries({ queryKey: ['summary', currentPetId] });
      }
    };

    window.addEventListener('storage', handlePetChange);
    window.addEventListener('petChanged', handlePetChange);

    return () => {
      window.removeEventListener('storage', handlePetChange);
      window.removeEventListener('petChanged', handlePetChange);
    };
  }, [petId, queryClient]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const matchesSearch = useCallback((m) => {
    const term = (searchTerm || '').toLowerCase();
    if (!term) return true;
    return (
      (m.medicationName || '').toLowerCase().includes(term) ||
      (m.condition || '').toLowerCase().includes(term)
    );
  }, [searchTerm]);

  const isActive = (m) => {
    const noStart = !m.startDate;
    const hasStartNoEnd = m.startDate && !m.endDate;
    const inFuture = m.startDate && m.endDate && new Date(m.endDate) > new Date();
    return noStart || hasStartNoEnd || inFuture;
  };

  const medications = useMemo(() => Array.isArray(summaryQuery.data?.medicationLogs) ? summaryQuery.data.medicationLogs : [], [summaryQuery.data]);
  const currentMeds = useMemo(() => medications.filter(isActive).filter(matchesSearch), [medications, matchesSearch]);
  const allMeds = useMemo(() => medications.filter(matchesSearch), [medications, matchesSearch]);

  const handleCopyMedications = () => {
    const meds = currentMeds || [];
    if (!Array.isArray(meds) || meds.length === 0) {
      navigator.clipboard.writeText('');
      return;
    }
    const lines = meds.map((med) => {
      const name = med?.medicationName || '-';
      const dosage = med?.dosage != null && med?.dosage !== '' ? med.dosage : '-';
      const unit = med?.unit ? ` ${med.unit}` : '';
      const hasFreq = med?.frequencyNumber != null && med?.frequencyNumber !== '';
      const freq = hasFreq ? `${med.frequencyNumber}${med?.frequencyUnit ? ` ${med.frequencyUnit}` : ''}` : t('medication.asNeeded');
      return `${name} ${dosage}${unit}/${freq}`.trim();
    });
    navigator.clipboard.writeText(lines.join(', '));
    if (copied) {
      setCopied(false);
      setTimeout(() => setCopied(true), 0);
    } else {
      setCopied(true);
    }
  };

  // Early returns for loading/error/empty
  const loading = summaryQuery.isLoading;
  if (loading) {
    return <CardSkeleton count={3} />;
  }
  if (error || summaryQuery.isError) {
    return <ErrorState />;
  }
  if ((medications || []).length === 0) {
    return (
      <NoEntries
        title={t('common.noMedicationTitle')}
        description={t('common.noMedicationDesc')}
        icon="medication"
      />
    );
  }

  return (
    <Box>
      {!loading && !error && (
        <SearchField 
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onClear={() => setSearchTerm('')}
          placeholder={t('medication.searchPlaceholder')}
          ariaLabel={t('medication.searchAriaLabel')}
        />
      )}

      {!loading && !error && (
        <Paper 
          sx={{ 
            mb: 3,
            p: 1,
            borderRadius: 3,
            background: 'transparent',
            border: `1px solid ${AppStyles.colors.border.light}`,
            boxShadow: AppStyles.shadows.sm,
          }}
        >
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange} 
            variant="fullWidth"
            sx={{
              minHeight: 48,
              '& .MuiTabs-flexContainer': { gap: 8 },
              '& .MuiTabs-indicator': { display: 'none' },
            }}
          >
            <Tab 
              label={t('medication.currentMedications')} 
              sx={{
                minHeight: 40,
                textTransform: 'none',
                borderRadius: 999,
                px: 2.5,
                color: AppStyles.colors.text.secondary,
                background: `linear-gradient(135deg, ${AppStyles.colors.primary.main}20 0%, ${AppStyles.colors.secondary.main}20 100%)`,
                fontWeight: AppStyles.typography.fontWeight.medium,
                '&:hover': {
                  background: `linear-gradient(135deg, ${AppStyles.colors.primary.main}30 0%, ${AppStyles.colors.secondary.main}30 100%)`,
                },
                '&.Mui-selected': {
                  color: AppStyles.colors.primary.contrastText,
                  background: `linear-gradient(135deg, ${AppStyles.colors.primary.main} 0%, ${AppStyles.colors.secondary.main} 100%)`,
                  boxShadow: AppStyles.shadows.sm,
                },
              }}
            />
            <Tab 
              label={t('medication.medicationsHistory')} 
              sx={{
                minHeight: 40,
                textTransform: 'none',
                borderRadius: 999,
                px: 2.5,
                color: AppStyles.colors.text.secondary,
                background: `linear-gradient(135deg, ${AppStyles.colors.primary.main}20 0%, ${AppStyles.colors.secondary.main}20 100%)`,
                fontWeight: AppStyles.typography.fontWeight.medium,
                '&:hover': {
                  background: `linear-gradient(135deg, ${AppStyles.colors.primary.main}30 0%, ${AppStyles.colors.secondary.main}30 100%)`,
                },
                '&.Mui-selected': {
                  color: AppStyles.colors.primary.contrastText,
                  background: `linear-gradient(135deg, ${AppStyles.colors.primary.main} 0%, ${AppStyles.colors.secondary.main} 100%)`,
                  boxShadow: AppStyles.shadows.sm,
                },
              }}
            />
          </Tabs>
        </Paper>
      )}

      {loading && (
        <CardSkeleton count={3} />
      )}
      {error && <ErrorState/>}

      {!loading && !error && activeTab === 0 && (
        <Paper 
          elevation={8}
          sx={{ 
            p: { xs: 2, md: 4 },
            borderRadius: 3,
            background: 'transparent',
            border: `1px solid ${AppStyles.colors.border.light}`,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: `linear-gradient(90deg, ${AppStyles.colors.secondary.main} 0%, ${AppStyles.colors.primary.main} 100%)`,
            }
          }}
        >
          <TableContainer sx={{ width: '100%' }}>
            <Table sx={{ width: '100%' }}>
              <TableHead>
                <TableRow sx={{ '& th': { color: AppStyles.colors.primary.dark, fontWeight: AppStyles.typography.fontWeight.medium, fontSize: '0.8rem' } }}>
                  <TableCell>{t('medication.medicationName')}</TableCell>
                  <TableCell>{t('medication.dosage')}</TableCell>
                  <TableCell>{t('medication.frequency')}</TableCell>
                  <TableCell>{t('medication.condition')}</TableCell>
                  <TableCell>{t('medication.startDate')}</TableCell>
                  <TableCell>{t('medication.endDate')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentMeds.map((m, index) => (
                  <TableRow 
                    key={index}
                    sx={{
                      '& td': {
                        borderBottom: `1px solid ${AppStyles.colors.border.light}`,
                      },
                    }}
                  >
                    <TableCell>
                      <Typography 
                        variant="body1"
                        sx={{ 
                          color: AppStyles.colors.text.primary,
                          fontWeight: AppStyles.typography.fontWeight.medium,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {m.medicationName || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: AppStyles.colors.text.secondary }}>
                        {(m.dosage ?? '-')}{m.unit ? ` ${m.unit}` : ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: AppStyles.colors.text.secondary }}>
                        {m.frequencyNumber ?? t('medication.asNeeded')} {m.frequencyUnit || ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-start', width: '100%' }}>
                        <Chip 
                          label={m.condition || '-'}
                          size="small"
                          sx={{
                            borderRadius: 999,
                            px: 1,
                            height: 24,
                            background: `linear-gradient(135deg, ${AppStyles.colors.success.main} 0%, ${AppStyles.colors.success.light} 100%)`,
                            color: AppStyles.colors.success.contrastText,
                            border: `1px solid ${AppStyles.colors.success.light}`,
                            fontWeight: AppStyles.typography.fontWeight.medium,
                            boxShadow: AppStyles.shadows.sm,
                            textTransform: 'capitalize',
                          }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: AppStyles.colors.text.secondary }}>
                        {m.startDate ? moment(m.startDate).format('DD MMM YYYY') : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: AppStyles.colors.text.secondary }}>
                        {m.endDate ? moment(m.endDate).format('DD MMM YYYY') : '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Divider sx={{ mt: 1, borderColor: AppStyles.colors.border.light, opacity: 0.6 }} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopyMedications}
              sx={{
                borderRadius: 999,
                textTransform: 'none',
                fontWeight: AppStyles.typography.fontWeight.medium,
                backgroundColor: 'rgba(188, 185, 118, 0.12)',
                color: AppStyles.colors.primary.dark,
                borderColor: 'rgba(188, 185, 118, 0.35)',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: 'rgba(188, 185, 118, 0.18)',
                  borderColor: 'rgba(188, 185, 118, 0.45)',
                  boxShadow: 'none',
                }
              }}
            >
              {t('medication.copy')}
            </Button>
          </Box>
        </Paper>
      )}

      {!loading && !error && activeTab === 1 && (
        <Paper 
          elevation={8}
          sx={{ 
            p: { xs: 2, md: 4 },
            borderRadius: 3,
            background: 'transparent',
            border: `1px solid ${AppStyles.colors.border.light}`,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: `linear-gradient(90deg, ${AppStyles.colors.secondary.main} 0%, ${AppStyles.colors.primary.main} 100%)`,
            }
          }}
        >
          <TableContainer sx={{ width: '100%' }}>
            <Table sx={{ width: '100%' }}>
              <TableHead>
                <TableRow sx={{ '& th': { color: AppStyles.colors.primary.dark, fontWeight: AppStyles.typography.fontWeight.medium, fontSize: '0.8rem' } }}>
                  <TableCell>{t('medication.medicationName')}</TableCell>
                  <TableCell>{t('medication.dosage')}</TableCell>
                  <TableCell>{t('medication.frequency')}</TableCell>
                  <TableCell>{t('medication.condition')}</TableCell>
                  <TableCell>{t('medication.startDate')}</TableCell>
                  <TableCell>{t('medication.endDate')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allMeds.map((m, index) => (
                  <TableRow 
                    key={index}
                    sx={{
                      '& td': {
                        borderBottom: `1px solid ${AppStyles.colors.border.light}`,
                      },
                    }}
                  >
                    <TableCell>
                      <Typography 
                        variant="body1"
                        sx={{ 
                          color: AppStyles.colors.text.primary,
                          fontWeight: AppStyles.typography.fontWeight.medium,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {m.medicationName || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: AppStyles.colors.text.secondary }}>
                        {(m.dosage ?? '-')}{m.unit ? ` ${m.unit}` : ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: AppStyles.colors.text.secondary }}>
                        {m.frequencyNumber ?? t('medication.asNeeded')} {m.frequencyUnit || ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-start', width: '100%' }}>
                        <Chip 
                          label={m.condition || '-'}
                          size="small"
                          sx={{
                            borderRadius: 999,
                            px: 1,
                            height: 24,
                            background: `linear-gradient(135deg, ${AppStyles.colors.success.main} 0%, ${AppStyles.colors.success.light} 100%)`,
                            color: AppStyles.colors.success.contrastText,
                            border: `1px solid ${AppStyles.colors.success.light}`,
                            fontWeight: AppStyles.typography.fontWeight.medium,
                            boxShadow: AppStyles.shadows.sm,
                            textTransform: 'capitalize',
                          }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: AppStyles.colors.text.secondary }}>
                        {m.startDate ? moment(m.startDate).format('DD MMM YYYY') : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: AppStyles.colors.text.secondary }}>
                        {m.endDate ? moment(m.endDate).format('DD MMM YYYY') : '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
      <Snackbar
        open={copied}
        onClose={() => setCopied(false)}
        autoHideDuration={5000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setCopied(false)} 
          severity="success" 
          variant="filled"
          sx={{
            background: `linear-gradient(135deg, ${AppStyles.colors.primary.main} 0%, ${AppStyles.colors.secondary.main} 100%)`,
            color: AppStyles.colors.primary.contrastText,
            boxShadow: AppStyles.shadows.sm,
            fontWeight: AppStyles.typography.fontWeight.medium,
          }}
        >
          {t('medication.medicationsCopied')}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Medication;
